import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Suspense, lazy } from "react";
import { ROUTE_PATHS } from "../../constants";
import { Box, CircularProgress } from "@mui/material";
import {
  getLocalizedPath,
  isValidLocale,
  DEFAULT_LOCALE,
} from "../../utils/routing";
import { getAccessAndRefreshToken } from "../../utils/auth/authStorage";
import { getLocaleFromStorage } from "../../utils/locale";

// IamShell must be lazy-loaded so that `impactomics-common-layout`'s
// synchronous i18n.init() side-effect is deferred until after the host
// app's own "./i18n" module body has already run and claimed the i18next
// instance. A direct import would fire the package init during module
// evaluation — before "./i18n" in src/index.tsx gets a turn.
const IamShell = lazy(() => import("../../layout/IamShell"));

const LoginWithEmail = lazy(() => import("../../screens/LoginWithEmail"));
const LoginWithMobile = lazy(() => import("../../screens/LoginWithMobile"));
const ForgotPassword = lazy(() => import("../../screens/ForgotPassword"));
const LoginAnotherWay = lazy(() => import("../../screens/LoginOptions"));
const OtpScreen = lazy(() => import("../../screens/OtpValidation"));
const SelectTenantScreen = lazy(() => import("../../screens/SelectTenant"));
// const Signup = lazy(() => import("../../screens/Signup"));
const LoginScreen = lazy(() => import("../../screens/Login"));
const DashboardScreen = lazy(() => import("../../screens/Dashboard"));
const ResetPasswordRoute = lazy(() => import("./ResetPasswordRoute"));
const ProfileInformation = lazy(
  () => import("../../screens/ProfileInformation/index.tsx")
);
const HomeScreen = lazy(() => import("../../screens/HomeScreen"));
const UserSetupScreen = lazy(() => import("../../screens/UserSetup"));
const AccountSetupScreen = lazy(() => import("../../screens/AccountSetup"));

// Helper to convert absolute paths to relative paths for nested routes
const getRelativePath = (path: string): string => {
  return path.startsWith("/") ? path.slice(1) : path;
};

/**
 * Wraps protected routes that require an authenticated session.
 * Login/OTP/ForgotPassword/UserSetup/AccountSetup routes remain public.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useParams<{ locale: string }>();
  const { accessToken } = getAccessAndRefreshToken();
  if (!accessToken || accessToken.trim() === '') {
    const currentLocale = (locale && isValidLocale(locale)) ? locale : (getLocaleFromStorage() || DEFAULT_LOCALE);
    const redirectPath = getLocalizedPath(ROUTE_PATHS.LOGIN, currentLocale);
    console.warn(
      '🔴 ProtectedRoute: No accessToken found — redirecting to login',
      {
        currentPathname: typeof window !== 'undefined' ? window.location.pathname : '(ssr)',
        currentSearch: typeof window !== 'undefined' ? window.location.search : '',
        detectedLocale: currentLocale,
        redirectPath,
        accessToken: accessToken ?? '(null/empty)',
      }
    );
    return <Navigate to={redirectPath} replace />;
  }
  console.log('🟢 ProtectedRoute: accessToken present — rendering protected content', {
    currentPathname: typeof window !== 'undefined' ? window.location.pathname : '(ssr)',
    tokenPreview: `${accessToken.slice(0, 20)}…`,
  });
  return <>{children}</>;
};

// Component to handle default route navigation with locale
const DefaultRoute = () => {
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = isValidLocale(locale) ? locale : DEFAULT_LOCALE;
  const localizedPath = getLocalizedPath(ROUTE_PATHS.LOGIN, currentLocale);
  return <Navigate to={localizedPath} replace />;
};

const AppRouter = () => {
  return (
    <Suspense
      fallback={
        <Box
          role="status"
          aria-live="polite"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress aria-label="Loading" />
        </Box>
      }
    >
      <Routes>
        <Route path=":locale" element={<Outlet />}>
          <Route
            path={getRelativePath(ROUTE_PATHS.LOGIN_EMAIL)}
            element={<LoginWithEmail />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.LOGIN_MOBILE)}
            element={<LoginWithMobile />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.FORGOT_PASSWORD)}
            element={<ForgotPassword />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.LOGIN_ANOTHER_WAY)}
            element={<LoginAnotherWay />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.OTP)}
            element={<OtpScreen />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.SELECT_TENANT)}
            element={<SelectTenantScreen />}
          />
          {/* <Route
            path={getRelativePath(ROUTE_PATHS.SIGNUP)}
            element={<Signup />}
          /> */}
          <Route
            path={getRelativePath(ROUTE_PATHS.LOGIN)}
            element={<LoginScreen />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.RESET_PASSWORD)}
            element={<ResetPasswordRoute />}
          />
          {/* Public invitation flows — authenticated via ?token= query param, not session */}
          <Route
            path={getRelativePath(ROUTE_PATHS.USER_SETUP)}
            element={<UserSetupScreen />}
          />
          <Route
            path={getRelativePath(ROUTE_PATHS.ACCOUNT_SETUP)}
            element={<AccountSetupScreen />}
          />

          {/* Protected routes rendered inside the common layout shell */}
          <Route element={<ProtectedRoute><IamShell /></ProtectedRoute>}>
            <Route
              path={getRelativePath(ROUTE_PATHS.DASHBOARD)}
              element={<DashboardScreen />}
            />
            <Route
              path={getRelativePath(ROUTE_PATHS.PROFILE)}
              element={<ProfileInformation />}
            />
            <Route
              path={getRelativePath(ROUTE_PATHS.HOME)}
              element={<HomeScreen />}
            />
          </Route>

          <Route path={ROUTE_PATHS.DEFAULT} element={<DefaultRoute />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
